import { Component, ComponentType, createElement } from 'react';
import { BraxLoading } from 'design-system';

import { Languages, RealLanguages } from '../../languages';
import IntlContext, { IntlContextValue, Language, setLanguage } from './IntlContext';
import { IntlConsumer } from './IntlConsumer';
import storage from '../storage';

export type IntlProviderProps = {
    languages: { [key in RealLanguages]: Language | (() => Promise<Language>) },
    children: ComponentType
};

type IntlProviderState = IntlContextValue;

export class IntlProvider extends Component<IntlProviderProps, IntlProviderState> {

    mounted = false;

    state: IntlProviderState = {
        loading: true,
        language: {},
        current: null,
        change: (language: Languages) => this.loadLanguage(language)
    };

    componentDidMount() {
        this.loadLanguage(storage.get('brax.user.language') as Languages | null || 'automatic');
    }

    getBrowserLanguage() {
        const languages = window.navigator.languages
            ? window.navigator.languages
        : window.navigator.language
            ? [window.navigator.language]
        : (window.navigator as any).userLanguage
            ? [(window.navigator as any as { userLanguage: string }).userLanguage]
            : ['en'];

        const availableLanguages = Object.keys(this.props.languages);

        let detectedLanguage = Object.keys(this.props.languages)[0] as RealLanguages;

        for (const language of languages) {
            if (availableLanguages.indexOf(language) !== -1) {
                detectedLanguage = language as RealLanguages;

                break;
            }

            const countryCode = language.split('-', 2)[0];

            if (countryCode !== language && availableLanguages.indexOf(language) !== -1) {
                detectedLanguage = countryCode as RealLanguages;

                break;
            }
        }

        return detectedLanguage;
    }

    async loadLanguage(name: Languages) {
        if (this.state.current === name)
            return;

        storage.set('brax.user.language', name);

        const languageCode = name === 'automatic' ? this.getBrowserLanguage() : name;

        let language;

        if (typeof this.props.languages[languageCode] === 'function') {
            this.setState({
                loading: true
            });

            language = await (this.props.languages[languageCode] as () => Promise<Language>)();
        } else {
            language = this.props.languages[languageCode] as Language;
        }

        setLanguage(languageCode, language);

        this.setState({
            loading: false,
            current: name,
            language: language
        });
    }

    render() {
        return (
            <IntlContext.Provider value={this.state}>
                <IntlConsumer>
                    { context => (<>
                        { createElement(this.props.children) }

                        { context.loading ? (
                            <BraxLoading />
                        ) : null }
                    </>) }
                </IntlConsumer>
            </IntlContext.Provider>
        );
    }

}
